// /auth/magic-callback — final hop in the magic-link sign-in web flow.
//
// Server side (server/src/domains/auth/controllers/AuthController.js
// confirmMagicLink): user clicks the link in their email →
// `<api>/auth/magic-link/confirm?token=<...>` → server validates the
// token, mints SDK access + refresh tokens, redirects to:
//
//   <shell>/auth/magic-callback#access_token=<...>&refresh_token=<...>
//                              &access_exp=<ms>&refresh_exp=<ms>
//                              &redirect=<optional>
//
// Tokens land in the URL HASH (not query) so they don't appear in
// HTTP logs or Referer headers as the page loads. We parse them out,
// hand them to the SDK's TokenManager (which persists to localStorage),
// hydrate the user via initializeUserSession, then strip the hash and
// route to `redirect` (clamped to a same-origin path) or `/`.
//
// Errors surface inline rather than silently bouncing — a missing
// token in the hash, an SDK persist failure, or a getMe() rejection
// all give the user a clear message + a back link.
//
// EVERY status this page shows is a bundle KEY in `s.statusKey`, resolved at
// render time — never a literal. This line of text IS the whole page while
// the handshake runs, so an English literal here left a Georgian arrival
// reading English from the first frame to the last, and no key gate could see
// it: the PROP check reads `text: '<literal>'`, and these sat behind a `||`
// inside a reactive function or under a plain state key. A failure is
// classified to a CODE by `functions/authCallbackError.js`; the SDK's own
// message goes to the console, never to the screen.
// (DESIGN-AUTH-CALLBACK-STATUS-TEXT-IS-ENGLISH-ONLY-ON-A-GEORGIAN-ARRIVAL-1;
// the gate is `pages/auth/callbackStatusI18n.test.js` plus this file's entry
// in `__tests__/i18nSharedExtraction.test.js` BRANCH_CLEAR.)

const _parseHashTokens = () => {
  if (typeof window === 'undefined') return null
  // Server uses `#access_token=...` (URL hash, not query) so secrets
  // don't appear in HTTP logs / Referer headers during the bounce.
  const hash = (window.location.hash || '').replace(/^#/, '')
  if (!hash) return null
  const params = new URLSearchParams(hash)
  const accessToken = params.get('access_token')
  const refreshToken = params.get('refresh_token')
  const accessExp = params.get('access_exp')
  if (!accessToken || !refreshToken) return null
  return {
    accessToken,
    refreshToken,
    accessExp: accessExp ? Number(accessExp) : null,
    refreshExp: params.get('refresh_exp') ? Number(params.get('refresh_exp')) : null,
    redirect: params.get('redirect')
  }
}

const _clearHash = () => {
  if (typeof window === 'undefined') return
  // Replace the URL without the hash so a refresh / back-button doesn't
  // re-fire the callback with the same single-use tokens. `replaceState`
  // avoids adding a history entry; the server's 302 already created one.
  try {
    const clean = `${window.location.pathname}${window.location.search}`
    window.history.replaceState(null, '', clean)
  } catch (_) {
    /* sandbox may throw — non-fatal */
  }
}

export const magicCallback = {
  extends: 'Page',
  metadata: (el) => ({ title: el.call('polyglot', 'auth.page.callback.title') }),
  flow: 'y',
  align: 'center center',
  padding: 'D C',
  width: '100%',
  height: '100dvh',
  maxWidth: 'none',

  Card: {
    flow: 'column',
    align: 'center center',
    gap: 'A',
    padding: 'C',
    round: 'radiusCard',
    theme: 'dialog',
    border: 'hairline solid blue.1',
    minWidth: 'F',

    Status: {
      tag: 'p',
      fontSize: 'A',
      // `statusKey` is a bundle KEY, never copy — see the note above.
      text: (el, s) => el.call('polyglot', s.statusKey || 'auth.callback.verifyingLink'),
      color: (el, s) => (s.error ? 'red' : 'fg.0')
    },

    onRender: async (el, s) => {
      // Idempotency guard — onRender can fire multiple times under HMR
      // and the tokens are single-use, so we mustn't replay them.
      if (s.exchangeStarted) return
      s.update({ exchangeStarted: true, statusKey: 'auth.callback.persisting', error: false })

      const parsed = _parseHashTokens()
      if (!parsed) {
        s.update({ error: true, statusKey: 'auth.callback.error.linkMalformed' })
        return
      }
      const { accessToken, refreshToken, accessExp, redirect } = parsed
      _clearHash()

      try {
        // SDK doesn't expose a public setTokens — go through TokenManager
        // directly. This matches what AuthService.googleAuth /
        // confirmRegistration / etc. do internally on a successful login.
        const sdk = el.sdk
        const tokenManager = sdk?._tokenManager
        if (!tokenManager?.setTokens) {
          throw new Error('SDK token manager unavailable')
        }
        const expiresIn = accessExp
          ? Math.max(0, Math.floor((accessExp - Date.now()) / 1000))
          : null
        tokenManager.setTokens({
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: expiresIn,
          token_type: 'Bearer'
        })
        // A sign-in on this origin: its refresh token is its own (review D1).
        el.call('markOwnSession')

        s.update({ statusKey: 'auth.callback.loadingAccount' })
        // getMe() is the standard hydration call — bootShell uses the
        // same one. Wrapping in try/catch so a transient network blip
        // doesn't strand the user with valid tokens but no profile.
        let user = null
        try {
          user = await sdk.getMe?.()
        } catch (err) {
          console.warn('[magic-callback] getMe failed (tokens still valid):', err?.message || err)
        }

        if (user) {
          await el.call('initializeUserSession', {
            loginData: {
              user,
              tokens: {
                accessToken,
                refreshToken,
                accessTokenExp: accessExp ? { expiresAt: accessExp } : undefined
              }
            }
          })
        }

        s.update({ statusKey: 'auth.callback.redirecting' })
        // `redirect` comes from a hash anyone can craft: clamp it to a
        // same-origin path. A `javascript:` value would run script on this
        // origin right after the session swap; an absolute URL would be an
        // open redirect. consumeOAuthNext answers '/' for both.
        const next = el.call('consumeOAuthNext', redirect) || '/'
        // Use top-level navigation so the SDK reads the freshly-persisted
        // localStorage tokens via its normal init path on the next page —
        // matches what /github-auth-callback does.
        window.location.replace(next)
      } catch (err) {
        console.error('[magic-callback] sign-in failed:', err)
        s.update({
          error: true,
          statusKey: el.call('authCallbackErrorKey', err)
        })
      }
    },

    BackLink: {
      extends: 'Link',
      href: '/login',
      text: '{{ auth.callback.backToSignIn | polyglot }}',
      show: (el, s) => !!s.error,
      fontSize: 'Z2'
    }
  }
}
